import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "options", "line1", "line2", "city", "postcode", "countryCode", "lat", "lng"]
  static values = {
    hideDropdownClass: String,
    searchOptionClass: String,
    highlightedOptionClass: String
  }

  connect() {
    this.locationiqKey = "pk.0f6422d36f7be333dd289765f703652f"
    this.results = []
    this.highlightedIndex = -1
    this.isSelectingResult = false
    this.debouncedSearch = window.helpers.debounce(this.performSearch.bind(this), 500)
    this.resetHandler = this.resetCoordinates.bind(this)
    this.setupAddressFieldListeners()
  }

  disconnect() {
    this.addressFields.forEach(field => {
      field.removeEventListener("blur", this.resetHandler)
    })
  }

  get addressFields() {
    return [
      this.hasLine1Target && this.line1Target,
      this.hasCityTarget && this.cityTarget,
      this.hasPostcodeTarget && this.postcodeTarget,
      this.hasCountryCodeTarget && this.countryCodeTarget
    ].filter(Boolean)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  search(event) {
    const query = event.target.value.trim()

    if (query.length < 2) {
      this.hideDropdown()
      return
    }

    this.debouncedSearch(query)
  }

  performSearch(query) {
    const params = new URLSearchParams({
      key: this.locationiqKey,
      q: query,
      limit: 5,
      countrycodes: "GB,IE",
      tag: "place:*,addr:*,highway:*"
    })

    fetch(`https://eu1.locationiq.com/v1/autocomplete.php?${params}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(data => this.handleResults(data))
      .catch(error => this.handleError(error))
  }

  handleResults(data) {
    this.results = data || []

    if (this.results.length === 0) {
      this.hideDropdown()
      return
    }

    this.renderResults()
    this.showDropdown()
  }

  renderResults() {
    this.optionsTarget.innerHTML = ""
    this.highlightedIndex = -1

    this.results.forEach((result, index) => {
      const li = document.createElement("li")
      li.className = this.searchOptionClassValue
      li.textContent = result.display_name
      li.dataset.index = index
      li.addEventListener("mousedown", (e) => {
        e.preventDefault()
        this.selectResult(index)
      })
      li.addEventListener("mouseenter", () => {
        this.highlightOption(index)
      })
      this.optionsTarget.appendChild(li)
    })
  }

  navigateOptions(event) {
    if (!this.isDropdownVisible()) return

    const { upKey, downKey, enterKey, escapeKey } = global.helpers.keyCodes

    switch (event.keyCode) {
      case upKey:
        event.preventDefault()
        this.highlightPrevious()
        break
      case downKey:
        event.preventDefault()
        this.highlightNext()
        break
      case enterKey:
        event.preventDefault()
        if (this.highlightedIndex >= 0) {
          this.selectResult(this.highlightedIndex)
        }
        break
      case escapeKey:
        this.hideDropdown()
        break
    }
  }

  highlightOption(index) {
    const options = this.optionsTarget.querySelectorAll(this.searchOptionClassValue)
    const highlightedClass = this.highlightedOptionClassValue
    options.forEach((opt, i) => {
      opt.classList.toggle(highlightedClass, i === index)
    })
    this.highlightedIndex = index
  }

  highlightNext() {
    const nextIndex = Math.min(this.highlightedIndex + 1, this.results.length - 1)
    this.highlightOption(nextIndex)
  }

  highlightPrevious() {
    const prevIndex = Math.max(this.highlightedIndex - 1, 0)
    this.highlightOption(prevIndex)
  }

  selectResult(index) {
    const result = this.results[index]
    if (!result) return

    this.isSelectingResult = true
    const address = result.address || {}

    if (this.hasLine1Target) {
      this.line1Target.value = address.road || address.name || ""
      this.line1Target.dispatchEvent(new Event("input", { bubbles: true }))
    }

    if (this.hasLine2Target) {
      this.line2Target.value = ""
      this.line2Target.dispatchEvent(new Event("input", { bubbles: true }))
    }

    if (this.hasCityTarget) {
      this.cityTarget.value = address.city || address.town || address.village || ""
      this.cityTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    if (this.hasPostcodeTarget) {
      this.postcodeTarget.value = address.postcode || ""
      this.postcodeTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    if (this.hasCountryCodeTarget && address.country_code) {
      this.countryCodeTarget.value = address.country_code.toUpperCase()
      this.countryCodeTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    if (this.hasLatTarget) {
      this.latTarget.value = result.lat || ""
    }

    if (this.hasLngTarget) {
      this.lngTarget.value = result.lon || ""
    }

    this.inputTarget.value = result.display_name
    this.hideDropdown()

    if (this.hasLine1Target) {
      this.line1Target.focus()
    }

    this.isSelectingResult = false
  }

  dismissOptions() {
    setTimeout(() => {
      this.hideDropdown()
    }, 200)
  }

  showDropdown() {
    this.dropdownTarget.classList.remove(this.hideDropdownClassValue)
  }

  hideDropdown() {
    this.dropdownTarget.classList.add(this.hideDropdownClassValue)
    this.highlightedIndex = -1
  }

  isDropdownVisible() {
    return !this.dropdownTarget.classList.contains(this.hideDropdownClassValue)
  }

  handleError(error) {
    console.error("LocationIQ API error:", error)
    this.hideDropdown()
  }

  resetCoordinates() {

    if (this.isSelectingResult) return

    if (this.hasLatTarget) {
      this.latTarget.value = ""
    }
    if (this.hasLngTarget) {
      this.lngTarget.value = ""
    }
  }

  setupAddressFieldListeners() {
    this.addressFields.forEach(field => {
      field.addEventListener("blur", this.resetHandler)
    })
  }
}
